import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Typography,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Box,
  Tooltip,
  Divider
} from "@mui/material";
import { Add, Refresh, LibraryAdd } from "@mui/icons-material";
import { useTranslation } from "react-i18next";

import {
  getAnonymizationProfiles,
  createAnonymizationProfile,
  updateAnonymizationProfile,
  deleteAnonymizationProfile,
} from "../../services/anonymizationProfilesService";

import { PROCESS_TYPES } from "../../constants/processTypes";
import AnonymizationProfileFormDialog from "./AnonymizationProfileFormDialog/AnonymizationProfileFormDialog";
import AnonymizationProfileCard from "./AnonymizationProfileCard";

const AnonymizationProfilesPanel = ({ processType = PROCESS_TYPES.ANONYMIZATION }) => {
  const { t } = useTranslation();

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  const isPseudo = processType === PROCESS_TYPES.PSEUDONYMIZATION;

  const uiText = useMemo(() => {
    if (isPseudo) {
      return {
        title: t("pseudonymizationProfiles.title", "Profily pseudonymizácie"),
        subtitle: t("pseudonymizationProfiles.subtitle", "Ulož si rôzne sady pravidiel pseudonymizácie pre opakované použitie."),
        empty: t("pseudonymizationProfiles.empty", "Zatiaľ nemáš vytvorený žiadny profil pseudonymizácie."),
        loadError: t("pseudonymizationProfiles.loadError", "Nepodarilo sa načítať profily pseudonymizácie."),
        saveError: t("pseudonymizationProfiles.saveError", "Nepodarilo sa uložiť profil pseudonymizácie."),
        deleteError: t("pseudonymizationProfiles.deleteError", "Nepodarilo sa odstrániť profil pseudonymizácie."),
        deleteConfirm: (name) => t("pseudonymizationProfiles.deleteConfirm", 'Naozaj chceš odstrániť profil pseudonymizácie "{{name}}"?', { name }),
      };
    }
    return {
      title: t("anonymizationProfiles.title", "Anonymizačné profily"),
      subtitle: t("anonymizationProfiles.subtitle", "Ulož si rôzne sady pravidiel anonymizácie pre opakované použitie."),
      empty: t("anonymizationProfiles.empty", "Zatiaľ nemáš vytvorený žiadny anonymizačný profil."),
      loadError: t("anonymizationProfiles.loadError", "Nepodarilo sa načítať anonymizačné profily."),
      saveError: t("anonymizationProfiles.saveError", "Nepodarilo sa uložiť anonymizačný profil."),
      deleteError: t("anonymizationProfiles.deleteError", "Nepodarilo sa odstrániť anonymizačný profil."),
      deleteConfirm: (name) => t("anonymizationProfiles.deleteConfirm", 'Naozaj chceš odstrániť anonymizačný profil "{{name}}"?', { name }),
    };
  }, [isPseudo, t]);

  const loadProfiles = useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await getAnonymizationProfiles(processType);
      setProfiles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setError(uiText.loadError);
    } finally {
      setLoading(false);
    }
  }, [processType, uiText.loadError]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  const handleCreateClick = () => {
    setEditingProfile(null);
    setDialogOpen(true);
  };

  const handleEditClick = (profile) => {
    setEditingProfile(profile);
    setDialogOpen(true);
  };

  const handleDeleteClick = async (profile) => {
    const confirmed = window.confirm(uiText.deleteConfirm(profile.name));
    if (!confirmed) return;
    try {
      setError("");
      await deleteAnonymizationProfile(profile.id);
      await loadProfiles();
    } catch (err) {
      console.error(err);
      setError(uiText.deleteError);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingProfile(null);
  };

  const handleDialogSave = async (payload) => {
    try {
      setError("");
      if (editingProfile) {
        await updateAnonymizationProfile(editingProfile.id, payload);
      } else {
        await createAnonymizationProfile(payload, processType);
      }
      setDialogOpen(false);
      setEditingProfile(null);
      await loadProfiles();
    } catch (err) {
      console.error(err);
      setError(uiText.saveError);
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>{uiText.title}</Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
            {uiText.subtitle}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: 'center' }}>
          <Tooltip title={t("anonymizationProfiles.refresh", "Obnoviť zoznam")}>
            <span>
              <IconButton 
                onClick={loadProfiles} 
                disabled={loading}
                sx={{ bgcolor: 'action.hover' }}
              >
                <Refresh />
              </IconButton>
            </span>
          </Tooltip>

          <Button
            variant="contained"
            disableElevation
            startIcon={<Add />}
            onClick={handleCreateClick}
            sx={{ borderRadius: 2, px: 3, py: 1, fontWeight: 600 }}
          >
            {t("anonymizationProfiles.createButton", "Nový profil")}
          </Button>
        </Box>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: '200px' }}>
          <CircularProgress size={40} thickness={4} />
        </Box>
      )}

      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>
      )}

      {!loading && profiles.length === 0 && !error && (
        <Box 
            sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center',
                py: 8,
                px: 2,
                bgcolor: 'action.selected',
                borderRadius: 3,
                border: '1px dashed',
                borderColor: 'divider'
            }}
        >
            <LibraryAdd sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.primary" gutterBottom>
              {uiText.empty}
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3, maxWidth: 400 }}>
              Kliknutím na tlačidlo "Nový profil" si môžete vytvoriť svoju prvú sadu pravidiel.
            </Typography>
            <Button variant="outlined" startIcon={<Add />} onClick={handleCreateClick} sx={{ borderRadius: 2 }}>
                Vytvoriť prvý profil
            </Button>
        </Box>
      )}

      {!loading && profiles.length > 0 && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, minmax(0, 1fr))",
              lg: "repeat(3, minmax(0, 1fr))",
            },
            gap: 3,
          }}
        >
          {profiles.map((profile) => (
            <AnonymizationProfileCard
              key={profile.id}
              profile={profile}
              onEdit={handleEditClick}
              onDelete={handleDeleteClick}
            />
          ))}
        </Box>
      )}

      <AnonymizationProfileFormDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onSave={handleDialogSave}
        initialData={editingProfile}
        processType={processType}
      />
    </Box>
  );
};

export default AnonymizationProfilesPanel;